<template>
	<div class="relative flex flex-col" @click="selectedOrder = {}">
		<app-header-teleport>
			<div class="flex flex-1 flex-wrap justify-between">
				<div class="flex items-center gap-2">
					<app-title :title="$t('orders')" />
					<div>
						<app-button
							v-if="isManagingOneBusiness"
							icon="plus"
							class="btn btn-primary transparent small"
							@click="createOrder"
						/>
						<app-dropdown-button
							v-else
							:label="$t('add_order')"
							size="small"
							type="primary"
							label-prop="restaurantName"
							:items="user.restaurants"
							@click="(item) => createOrder(item.restaurantID)"
						/>
					</div>
					<app-button
						icon="arrows-cw"
						class="btn btn-success transparent small"
						@click="onRefresh"
						:loading="loading"
					/>
					<div>
						<app-advanced-filter @onFilter="onRefresh" @onClear="onClear" :loading="loading">
							<div v-if="renderFilters">
								<app-city-select id="cityFilter" v-model="filters.cityId" />
								<app-input type="text" name="Name" v-model="filters.orderId" placeholder="Order" />
							</div>
							<div v-else />
						</app-advanced-filter>
					</div>
					<div
						class="flex items-center gap-2 mx-1"
						v-if="extraDeliveryBusiness?.extraDeliveryTime?.extraTime"
					>
						<div
							class="flex items-center btn btn-primary cursor-pointer rounded-md px-4 gap-2"
							style="width: fit-content"
							@click="showDelayModal = true"
						>
							<app-icon icon="attention" class="text-white animate-pulse" />
							<span class="text-white animate-pulse"
								>{{ $t('delay') }}: +{{ extraDeliveryBusiness.extraDeliveryTime.extraTime }} min
								{{ $t('until') }}
								{{ $filters.getShortTime(extraDeliveryBusiness.extraDeliveryTime.endDate) }}</span
							>
						</div>
					</div>
					<app-button
						v-if="!extraDeliveryBusiness?.extraDeliveryTime?.extraTime && canEditExtraDeliveryTime"
						icon="clock"
						class="btn btn-primary transparent small"
						@click="showDelayModal = true"
					/>
				</div>
				<app-digital-time />
			</div>
		</app-header-teleport>
		<div class="hidden sm:flex items-center mt-2" v-if="renderFilters">
			<app-icon icon="filters" class="mr-1" />
			<span class="mr-2">{{ $t('quick_filters') }}:</span>
			<app-city-select
				id="quickCity"
				class="w-32 mr-1"
				:modelValue="filters.cityId"
				@update:modelValue="(val) => updateFilter('cityId', val)"
			/>
			<app-select
				class="mr-1 w-48"
				:placeholder="$t('delivery_type')"
				:can-deselect="false"
				:options="takeawayOptions"
				v-model="filters.takeaway"
				@select="updateFilter('takeaway', $event)"
			/>
		</div>
		<div class="flex flex-1 overflow-x-auto relative">
			<kanban-column
				class="flex-1"
				v-for="(value, name, index) in orders"
				:key="index"
				:orders="value"
				:status="name"
				@onStatusChange="changeStatus"
				@onAssignCourier="showCouriersModal = true"
				@answer="answerOrder"
				@cookOrder="cookOrder"
				@deliverOrder="deliverOrder"
			/>
			<!--			<div-->
			<!--				class="w-1/4 absolute bottom-0 left-0 bg-p-light cursor-pointer p-6 rounded-md flex items-center gap-3"-->
			<!--			>-->
			<!--				<app-icon icon="calendar" class="text-3xl" />-->
			<!--				<span>Show Scheduled Orders ({{ scheduledOrdersNr }})</span>-->
			<!--			</div>-->
		</div>
		<transition name="slide-fade-reverse">
			<order-details-floating-card
				v-if="selectedOrder.orderID"
				:order="selectedOrder"
				@onEditAddress="showAddressModal = true"
			/>
		</transition>
	</div>
	<extra-delivery-time-modal
		v-if="showDelayModal && canEditExtraDeliveryTime"
		:business="extraDeliveryBusiness"
		@close="showDelayModal = false"
	/>
</template>

<script>
	import AppAdvancedFilter from '@/components/app/AppAdvancedFilter';
	import KanbanColumn from '../../components/orders/kanban/KanbanColumn';
	import AppDigitalTime from '@/components/app/AppDigitalTime';
	import OrderService from '@/services/OrderService';
	import constants from '@/constants/orders';
	import OrderDetailsFloatingCard from '@/components/orders/kanban/OrderDetailsFloatingCard';
	import RouteNames from '@/router/names';
	import AppIcon from '@/components/app/AppIcon';
	import AppCitySelect from '@/components/app/AppCitySelect';
	import { mapGetters } from 'vuex';
	import AppDropdownButton from '@/components/app/AppDropdownButton';
	import ExtraDeliveryTimeModal from '@/components/ExtraDeliveryTimeModal.vue';
	import BusinessService from '@/services/sales/BusinessService';

	export default {
		name: 'OrdersKanban',
		components: {
			ExtraDeliveryTimeModal,
			AppDropdownButton,
			AppCitySelect,
			AppIcon,
			KanbanColumn,
			AppAdvancedFilter,
			AppDigitalTime,
			OrderDetailsFloatingCard,
		},
		data: () => ({
			businessBaboonCouriers: [
				{ value: 1, label: 'With Baboon Courier' },
				{ value: '0', label: 'Without Baboon Courier' },
				{ value: -1, label: 'All' },
			],
			filters: {
				cityId: -1,
				takeaway: -1,
			},
			getOrdersInterval: null,
			orders: [],
			loading: false,
			exporting: false,
			selectedOrder: {},
			showCouriersModal: false,
			showAddressModal: false,
			renderFilters: true,
			scheduledOrdersNr: 5,
			takeawayOptions: [...constants.deliveryTypesOptions],
			refreshTime: 60000,
			showDelayModal: false,
		}),
		computed: {
			...mapGetters({
				selectedRestaurants: 'selectedRestaurants',
				user: 'user',
			}),
			isManagingOneBusiness() {
				return this.user?.restaurants?.length === 1;
			},
			extraDeliveryBusiness() {
				return this.user.restaurants?.length === 1
					? this.user.restaurants[0]
					: this.user.restaurants?.find((rest) => {
							return rest.extraDeliveryTime.extraTime > 0;
					  }) || this.user.restaurants[0];
			},
			canEditExtraDeliveryTime() {
				return BusinessService.canEditExtraDeliveryTime();
			},
		},
		methods: {
			createOrder(businessId = this.user?.restaurants[0].restaurantID) {
				this.$router.push({ name: RouteNames.CreateOrder, params: { businessId } });
			},
			getOrders() {
				this.loading = true;
				OrderService.getKanbanOrders({
					...this.filters,
					businessIDs: this.selectedRestaurants.join('-'),
				})
					.then((res) => {
						this.orders = res;
						this.loading = false;
					})
					.catch((e) => this.$notificationService.error(e))
					.finally(() => this.emitter.emit('isLoading', false));
			},
			orderClick(order) {
				this.selectedOrder = this.selectedOrder.id === order.orderID ? {} : order;
			},
			changeStatus(orderId, status) {
				OrderService.changeOrderStatus(orderId, status)
					.then(() => {
						this.$notificationService.success(`Order #${this.selectedOrder.orderID} updated Successfully!`);
						this.selectedOrder = {};
						this.getOrders();
					})
					.catch((error) => {
						this.$notificationService.error(error);
					});
			},
			onRefresh() {
				this.getOrders();
				this.reRenderFilters();
			},
			onClear() {
				this.filters = { ...constants.defaultFilters };
				this.onRefresh();
			},
			updateFilter(key, val) {
				this.filters[key] = val;
				this.$nextTick(() => {
					this.onRefresh();
				});
			},
			reRenderFilters() {
				this.renderFilters = false;
				this.$nextTick(() => {
					this.renderFilters = true;
				});
			},
			answerOrder(item) {
				this.emitter.emit('onNewOrderReceived', { orderId: item.orderID });
			},
			deliverOrder({ orderID }) {
				this.$store.dispatch('setLoadingOrderId', orderID);
				OrderService.deliverOrder(orderID)
					.then(() => {
						this.getOrders();
					})
					.catch((error) => {
						this.$notificationService.error(error);
					})
					.finally(() => {
						this.$store.dispatch('setLoadingOrderId', null);
					});
			},
			cookOrder({ orderID }) {
				this.$store.dispatch('setLoadingOrderId', orderID);
				OrderService.cookOrder(orderID)
					.then(() => {
						this.getOrders();
					})
					.catch((error) => {
						this.$notificationService.error(error);
					})
					.finally(() => {
						this.$store.dispatch('setLoadingOrderId', null);
					});
			},
			onNewOrderReceived() {},
		},
		mounted() {
			if (!this.orders.length) {
				this.emitter.emit('isLoading', true);
				this.getOrders();
			}
			this.getOrdersInterval = setInterval(() => {
				if (document.visibilityState === 'visible') {
					this.onRefresh();
				}
			}, this.refreshTime);
			this.emitter.on('onNewOrderReceived', () => {
				this.onNewOrderReceived();
			});
			this.emitter.on('updateLocalOrder', () => {
				this.getOrders();
			});
		},
		beforeUnmount() {
			clearInterval(this.getOrdersInterval);
			this.emitter.off('onNewOrderReceived', null);
			this.emitter.off('updateLocalOrder', null);
		},
		watch: {
			selectedRestaurants() {
				if (!this.$store.getters.token) return;
				this.getOrders();
			},
		},
	};
</script>

<style lang="scss" scoped></style>
